// AnswerBox.js
import React, { useState, useRef, useEffect } from "react";
import { Button, Alert, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useLang } from "../utils/LangContext";
import { postData } from "../utils/apiUtils";
import localStorageHelper from "../utils/localStorageHelper";
import ReactPlayer from "react-player";


const AnswerBox = ({
  currentQuestion,
  onAnswerSubmitted,
  setShowAnswerBox,
}) => {
  const { t } = useLang();
  const [previewImage, setPreviewImage] = useState("");
  const [localAnswer, setLocalAnswer] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [showAlertLocal, setShowAlertLocal] = useState(false);
  const [alertMessageLocal, setAlertMessageLocal] = useState("");

  // Audio ref
  const dingRef = useRef(null);
  const answerStartSentRef = useRef(false);

  // Initialize audio on component mount
  useEffect(() => {
    const audio = new Audio(require("../assets/audio/ding.mp3"));
    audio.preload = "auto";
    audio.volume = 0.7; // Set volume to 70%
    dingRef.current = audio;

    // Cleanup
    return () => {
      if (dingRef.current) {
        dingRef.current.pause();
        dingRef.current = null;
      }
    };
  }, []);

  // Play audio when modal is shown
  useEffect(() => {
    const playAudio = async () => {
      try {
        // Reset audio to beginning
        if (dingRef.current) {
          dingRef.current.currentTime = 0;
          await dingRef.current.play();
        }
      } catch (error) {
        console.log("Audio play failed (likely due to browser autoplay policy):", error);
      }
    };

    // Small delay to ensure modal is fully rendered
    const timer = setTimeout(() => {
      playAudio();
      
      // Vibrate if supported
      if (navigator.vibrate) {
        navigator.vibrate([120, 80, 120]);
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [currentQuestion?.relQuestion?.id]); // Trigger when question changes

  // Send answer-start data when modal opens
  useEffect(() => {
    if (answerStartSentRef.current) return;
    answerStartSentRef.current = true;

    const sendAnswerStart = async () => {
      try {
        const team = localStorageHelper.getItem("team");
        const game = localStorageHelper.getItem("game");
        
        if (!team || !game || !currentQuestion?.relQuestion?.id) {
          return;
        }

        const formData = new FormData();
        formData.append("question_id", currentQuestion.relQuestion.id);
        formData.append("game_hash", game.hash);
        formData.append("team_code", team.team_code);
        formData.append("team_member_id", team.team_member_id);

        const response = await postData("/api/v1/game/answer-start", formData);
        if (response.answerOK) {
          console.log("Answer start recorded successfully");
        }
      } catch (error) {
        // Do nothing on error as requested
        console.log("Answer start failed:", error);
      }
    };

    sendAnswerStart();
  }, [currentQuestion?.relQuestion?.id]); // Run once when question changes (modal opens)

  // helper to resize an image file to max width/height = 800px
  async function resizeImageFile(file, maxSize = 800) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        let { width, height } = img;
        if (width <= maxSize && height <= maxSize) {
          URL.revokeObjectURL(url);
          resolve(file);
          return;
        }
        const ratio = Math.min(maxSize / width, maxSize / height);
        width *= ratio;
        height *= ratio;
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            URL.revokeObjectURL(url);

            if (!blob) {
              reject(new Error("Canvas toBlob failed"));
              return;
            }

            const resizedFile = new File([blob], file.name, {
              type: "image/jpeg",
            });
            resolve(resizedFile);
          },
          "image/jpeg",
          0.8
        );
      };
      img.onerror = (e) => {
        URL.revokeObjectURL(url);
        reject(e);
      };
      img.src = url;
    });
  }

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const smallFile = await resizeImageFile(file, 800);
      setPreviewImage(URL.createObjectURL(smallFile));
      setLocalAnswer(smallFile);
    } catch (err) {
      console.error("Image resize failed:", err);
      setPreviewImage(URL.createObjectURL(file));
      setLocalAnswer(file);
    }
  };

  const handleRemoveImage = () => {
    setPreviewImage("");
    setLocalAnswer(null);

    const input = document.getElementById("icon-button-file");
    if (input) {
      input.value = "";
    }
  };

  const handleInputChange = (e) => setLocalAnswer(e.target.value);

  const handleCheckboxChange = (value, checked) => {
    const answers = Array.isArray(localAnswer) ? [...localAnswer] : [];

    if (checked) {
      answers.push(value);
    } else {
      const idx = answers.indexOf(value);
      if (idx > -1) answers.splice(idx, 1);
    }
    setLocalAnswer(answers);
  };

  const team = localStorageHelper.getItem("team");
  const game = localStorageHelper.getItem("game");

  const onSubmitHandler = async (e) => {
    e.preventDefault();
    if (submitted) return;
    if (!localAnswer || (Array.isArray(localAnswer) && !localAnswer.length)) {
      setAlertMessageLocal(t("noAnsw"));
      setShowAlertLocal(true);
      return;
    }

    setSubmitted(true);
    const formData = new FormData();
    let ans = localAnswer;

    if (localAnswer instanceof File) {
      formData.append("answer", "img");
      formData.append("file", localAnswer);
    } else {
      if (Array.isArray(localAnswer)) {
        ans = localAnswer.join("|");
      }
      formData.append("answer", ans);
    }

    formData.append("question_id", currentQuestion.relQuestion.id);
    formData.append("game_hash", game.hash);
    formData.append("team_code", team.team_code);
    formData.append("team_member_id", team.team_member_id);

    try {
      const response = await postData("/api/v1/game/answer-end", formData);

      if (response.answerOK) {
        onAnswerSubmitted(currentQuestion.relQuestion.id);
      } else {
        setAlertMessageLocal(response.error);
        setShowAlertLocal(true);
      }
    } catch (err) {
      console.error("Submission failed:", err);
    }

    setLocalAnswer(null);
    setPreviewImage("");
    setSubmitted(false);
  };

  const getAnswerText = (answer) => {
    const lang = localStorageHelper.getItem("currentLang");
    return (
      { et: answer.answer_et, en: answer.answer_en, ru: answer.answer_ru }[lang] ||
      answer.answer_et
    );
  };

  const getQuestionText = (question) => {
    const lang = localStorageHelper.getItem("currentLang");
    return (
      {
        et: question.relQuestion.question_et,
        en: question.relQuestion.question_en,
        ru: question.relQuestion.question_ru,
      }[lang] || question.relQuestion.question_et
    );
  };

  const renderInput = () => {
    switch (currentQuestion.relQuestion.type) {
      case "img":
        return (
          <>
            {previewImage && (
              <div className="smallMargin">
                <Button
                  variant="danger"
                  className="deleteBtn"
                  onClick={handleRemoveImage}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </Button>
                <img
                  src={previewImage}
                  alt="Uploaded"
                  className="addedImg"
                  style={{
                    maxWidth: "100%",
                    height: "auto",
                    display: "block",
                    margin: "0 auto",
                    borderRadius: "8px",
                  }}
                />
              </div>
            )}
            <input
              accept="image/*"
              capture="environment"
              id="icon-button-file"
              onChange={handleFileChange}
              type="file"
            />
          </>
        );
      case "textarea":
        return (
          <div className="form-group">
            <label htmlFor="textarea">{t("Your long answer")}</label>
            <textarea
              id="textarea"
              form="answers"
              placeholder={t("Enter your answer") + "..."}
              value={localAnswer || ""}
              onChange={handleInputChange}
              className="form-control"
            />
          </div>
        );
      case "radio":
        return currentQuestion.relQuestion.relQuestionAnswers.map((answer, idx) => (
          <div className="form-check" key={idx}>
            <input
              type="radio"
              id={`customRadio${idx}`}
              name="radio"
              value={getAnswerText(answer)}
              checked={localAnswer === getAnswerText(answer)}
              onChange={handleInputChange}
              className="form-check-input"
            />
            <label className="form-check-label" htmlFor={`customRadio${idx}`}>
              {getAnswerText(answer)}
            </label>
          </div>
        ));

      case "checkbox":
        return currentQuestion.relQuestion.relQuestionAnswers.map((answer, idx) => (
          <div className="form-check" key={idx}>
            <input
              type="checkbox"
              id={`check${idx}`}
              value={getAnswerText(answer)}
              checked={
                Array.isArray(localAnswer) &&
                localAnswer.includes(getAnswerText(answer))
              }
              onChange={(e) =>
                handleCheckboxChange(getAnswerText(answer), e.target.checked)
              }
              className="form-check-input"
            />
            <label className="form-check-label" htmlFor={`check${idx}`}>
              {getAnswerText(answer)}
            </label>
          </div>
        ));

      default:
        return null;
    }
  };

  return (
    <Modal
      show
      centered
      onHide={() => {
        setShowAnswerBox(false);
      }}
    >
      <Modal.Header closeButton>
        <Modal.Title>{getQuestionText(currentQuestion)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {showAlertLocal && (
          <Alert
            key={alertMessageLocal}
            variant="danger"
            style={{ whiteSpace: "normal", wordBreak: "break-word", color: "red" }}
          >
            {alertMessageLocal}
          </Alert>
        )}
        {currentQuestion.relQuestion.mediaType === "img" &&
          currentQuestion.relQuestion.mediaPath && (
            <div className="question-image">
              <img
                className="mediaShown img-fluid"
                src={currentQuestion.relQuestion.mediaPath}
                alt="media content"
              />
            </div>
          )}
        {currentQuestion.relQuestion.mediaType === "vid" &&
          currentQuestion.relQuestion.mediaPath && (
            <ReactPlayer
              className="mediaShown"
              url={currentQuestion.relQuestion.mediaPath}
              playing
              controls
              config={{ file: { attributes: { controlsList: "nodownload" } } }}
            />
          )}
        {renderInput()}
      </Modal.Body>
      <Modal.Footer>
        <Button onClick={onSubmitHandler}>{t("Submit")}</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AnswerBox;